import React, { useState } from 'react';
import { Mail, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { motion } from 'framer-motion';

interface ForgotPasswordFormProps {
  onBack: () => void;
  mode: 'password' | 'username';
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack, mode }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Check if email exists first
      const base = import.meta.env.VITE_SUPABASE_URL!;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY!;
      const checkRes = await fetch(`${base}/functions/v1/check-email-exists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anon}`,
        },
        body: JSON.stringify({ email: email.trim() })
      });

      if (!checkRes.ok) {
        throw new Error('Unable to verify email address. Please try again.');
      }

      const checkData = await checkRes.json();

      if (!checkData.exists) {
        throw new Error('Email address not recognized. Please check your email or sign up for a new account.');
      }

      // Email exists, proceed with password reset or username reminder
      if (mode === 'password') {
        // Reset password - redirect to auth callback which will detect recovery type
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback`,
        });

        if (resetError) {
          throw resetError;
        }
      } else {
        // Remind username
        // In a real app, this would call a server function to look up the username
        // and send it via email. For this demo, we'll simulate success.

        // Simulate sending username reminder email
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-theme-cards p-6 rounded-2xl shadow-card border border-theme-cards max-w-md mx-auto">
      <button
        onClick={onBack}
        className="flex items-center text-secondary hover:text-secondary-600 mb-6"
      >
        <ArrowLeft size={18} className="mr-1" />
        Back to Login
      </button>

      <h2 className="text-2xl font-bold text-center mb-6 text-contrast">
        {mode === 'password' ? 'Reset Password' : 'Recover Username'}
      </h2>

      {success ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-accent2" />
          </div>
          <h3 className="text-xl font-semibold mb-2 text-contrast">
            {mode === 'password' ? 'Password Reset Email Sent' : 'Username Reminder Sent'}
          </h3>
          <p className="text-contrast-400 mb-6">
            {mode === 'password'
              ? 'We\'ve sent instructions to reset your password to your email address. Please check your inbox.'
              : 'We\'ve sent your username to your email address. Please check your inbox.'}
          </p>
          <button
            onClick={onBack}
            className="bg-secondary text-white px-6 py-3 rounded-md hover:bg-secondary-600 transition-colors"
          >
            Return to Login
          </button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit}>
          <p className="text-contrast-400 mb-6 text-center">
            {mode === 'password'
              ? 'Enter your email address and we\'ll send you instructions to reset your password.'
              : 'Enter your email address and we\'ll send you a reminder of your username.'}
          </p>

          {error && (
            <div className="bg-secondary/10 text-secondary p-3 rounded-md mb-4 flex items-start">
              <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-contrast mb-1">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-contrast-500" />
              </div>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-secondary text-white py-3 rounded-md hover:bg-secondary-600 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                  <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              mode === 'password' ? 'Send Reset Instructions' : 'Send Username Reminder'
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordForm;